import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../helpers/axios';

// Shared style for each settings card container
const cardStyle = {
  flex: 1,
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '20px',
  backgroundColor: '#fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

// Shared style for form field labels
const labelStyle = {
  display: 'block',
  fontSize: '12px',
  color: '#667085',
  marginBottom: '4px',
};

// Shared style for text inputs and select dropdowns
const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #d0d5dd',
  borderRadius: '6px',
  fontSize: '13px',
  color: '#344054',
  boxSizing: 'border-box',
};

// Shared style for Save buttons inside each card
const saveButtonStyle = {
  width: '100%',
  padding: '9px 0',
  backgroundColor: '#d32028',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontFamily: 'Inter-Medium',
  fontSize: '13px',
  cursor: 'pointer',
};

export default function ManagerSettings() {
  const [loading, setLoading] = useState(true);

  // Editable form values for each settings card
  // These are pre-filled from the GET response on mount
  const [rotationForm, setRotationForm] = useState({
    frequencyDays: 14,
    day: 1,
    time: '00:00',
  });

  const [ptpForm, setPtpForm] = useState({
    extensionDays: 14,      // how many days a PTP blocks rotation
    maxConsecutive: 2,      // max back-to-back PTPs allowed per loan
  });

  const [allocationForm, setAllocationForm] = useState({
    method: 'round_robin',
    triggerEvent: 'daily_cron',
  });

  // Add-agent input field value
  const [newAgentEmail, setNewAgentEmail] = useState('');
  const [addingAgent, setAddingAgent] = useState(false);

  useEffect(() => {
    // One GET call returns all settings: rotation, ptp, allocation, agentPools
    axiosInstance
      .get('/collection-incentive/settings')
      .then((res) => {
        const settingsData = res.data.data;

        // Pre-fill each form with values from the server
        setRotationForm({
          frequencyDays: settingsData.rotation.frequencyDays,
          day: settingsData.rotation.day,
          time: settingsData.rotation.time,
        });

        setPtpForm({
          extensionDays: settingsData.ptp.extensionDays,
          maxConsecutive: settingsData.ptp.maxConsecutive,
        });

        setAllocationForm({
          method: settingsData.allocation.method,
          triggerEvent: settingsData.allocation.triggerEvent,
        });

      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load settings');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ── Save handlers — each card has its own PUT endpoint ──

  const handleSaveRotation = () => {
    axiosInstance
      .put(
        '/collection-incentive/settings/rotation',
        {
          frequencyDays: Number(rotationForm.frequencyDays),
          day: Number(rotationForm.day),
          time: rotationForm.time,
        }
      )
      .then(() => {
        alert('Rotation settings saved successfully');
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to save rotation settings');
      });
  };

  const handleSavePtp = () => {
    // PTP extension duration and max consecutive limits are two separate PUT endpoints
    const saveExtensionDays = axiosInstance.put(
      '/collection-incentive/settings/ptp-extension',
      { extensionDays: Number(ptpForm.extensionDays) }
    );

    const saveMaxConsecutive = axiosInstance.put(
      '/collection-incentive/settings/ptp-limits',
      { maxConsecutivePtp: Number(ptpForm.maxConsecutive) }
    );

    // Run both PUTs at the same time and wait for both to finish
    Promise.all([saveExtensionDays, saveMaxConsecutive])
      .then(() => {
        alert('PTP settings saved successfully');
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to save PTP settings');
      });
  };

  const handleSaveAllocation = () => {
    axiosInstance
      .put(
        '/collection-incentive/settings/allocation-method',
        {
          method: allocationForm.method,
          triggerEvent: allocationForm.triggerEvent,
        }
      )
      .then(() => {
        alert('Allocation settings saved successfully');
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to save allocation settings');
      });
  };

  const handleAddAgent = () => {
    if (!newAgentEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    setAddingAgent(true);

    // Backend looks up the user by email, validates role=collection, then adds to all 8 buckets
    axiosInstance
      .post(
        '/collection-incentive/settings/agents',
        { email: newAgentEmail.trim() }
      )
      .then((res) => {
        const addedToBuckets = res.data.data.addedToBuckets || [];

        setNewAgentEmail('');
        alert(`Agent added to buckets: ${addedToBuckets.join(', ')}`);
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to add agent');
      })
      .finally(() => {
        setAddingAgent(false);
      });
  };

  if (loading) {
    return <div style={{ padding: '20px', color: '#667085' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '10px' }}>

      {/* ── Row 1: Three settings cards side by side ── */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>

        {/* ── Rotation Settings Card ── */}
        <div style={cardStyle}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '15px', fontWeight: '600', color: '#344054', marginBottom: '16px' }}>
            Rotation Settings
          </p>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Frequency (days)</label>
            <select
              value={rotationForm.frequencyDays}
              onChange={(e) => setRotationForm({ ...rotationForm, frequencyDays: e.target.value })}
              style={inputStyle}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
            </select>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Day of Week (0 = Sunday … 6 = Saturday)</label>
            <input
              type="number"
              min={0}
              max={6}
              value={rotationForm.day}
              onChange={(e) => setRotationForm({ ...rotationForm, day: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Time (24-hour, HH:MM)</label>
            <input
              type="time"
              value={rotationForm.time}
              onChange={(e) => setRotationForm({ ...rotationForm, time: e.target.value })}
              style={inputStyle}
            />
          </div>

          <button onClick={handleSaveRotation} style={saveButtonStyle}>
            Save Rotation Settings
          </button>
        </div>

        {/* ── PTP Settings Card ── */}
        <div style={cardStyle}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '15px', fontWeight: '600', color: '#344054', marginBottom: '16px' }}>
            PTP Settings
          </p>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Extension Duration (days) — how long a PTP blocks rotation</label>
            <select
              value={ptpForm.extensionDays}
              onChange={(e) => setPtpForm({ ...ptpForm, extensionDays: e.target.value })}
              style={inputStyle}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Max Consecutive PTPs allowed per loan (1–5)</label>
            <input
              type="number"
              min={1}
              max={5}
              value={ptpForm.maxConsecutive}
              onChange={(e) => setPtpForm({ ...ptpForm, maxConsecutive: e.target.value })}
              style={inputStyle}
            />
          </div>

          <button onClick={handleSavePtp} style={saveButtonStyle}>
            Save PTP Settings
          </button>
        </div>

        {/* ── Allocation Settings Card ── */}
        <div style={cardStyle}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '15px', fontWeight: '600', color: '#344054', marginBottom: '16px' }}>
            Allocation Settings
          </p>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Allocation Method</label>
            <select
              value={allocationForm.method}
              onChange={(e) => setAllocationForm({ ...allocationForm, method: e.target.value })}
              style={inputStyle}
            >
              <option value="round_robin">Round Robin</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Trigger Event</label>
            <input
              type="text"
              value={allocationForm.triggerEvent}
              onChange={(e) => setAllocationForm({ ...allocationForm, triggerEvent: e.target.value })}
              style={inputStyle}
            />
          </div>

          <button onClick={handleSaveAllocation} style={saveButtonStyle}>
            Save Allocation Settings
          </button>
        </div>
      </div>

      {/* ── Row 2: Agent Pool Card (full width) ── */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '15px', fontWeight: '600', color: '#344054', marginBottom: '16px' }}>
          Agent Pool
        </p>

        {/* Add Agent row */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>
              Add Agent by Email — agent will be added to all buckets automatically
            </label>
            <input
              type="email"
              placeholder="Enter agent email address..."
              value={newAgentEmail}
              onChange={(e) => setNewAgentEmail(e.target.value)}
              style={inputStyle}
            />
          </div>
          <button
            onClick={handleAddAgent}
            disabled={addingAgent}
            style={{
              padding: '9px 24px',
              backgroundColor: addingAgent ? '#e0e0e0' : '#d32028',
              color: addingAgent ? '#667085' : 'white',
              border: 'none',
              borderRadius: '6px',
              fontFamily: 'Inter-Medium',
              fontSize: '13px',
              cursor: addingAgent ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {addingAgent ? 'Adding...' : 'Add Agent'}
          </button>
        </div>

      </div>

    </div>
  );
}
